import { useAnalytics } from '@/services/analytics';
import { Credit, getUserCredits } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { AxiosError } from 'axios';
import { useQuery, useQueryClient } from 'react-query';

export const CREDITS_KEY = 'CREDITS_KEY';
let creditsDone = false;

export function useCreditQuery({ enabled = true, includeExpired = false }: { enabled?: boolean; includeExpired?: boolean } = {}) {
    const mixpanel = useAnalytics();
    const { isAnonymous } = useAuth();
    const queryClient = useQueryClient();
    const queryKey = `${CREDITS_KEY}${includeExpired ? '_INCLUDE_EXPIRED' : ''}`;
    return useQuery<Credit>(queryKey, () => getUserCredits(includeExpired), {
        placeholderData: { remaining: 0, total: 1, expire_at: '', isExpired: false },
        staleTime: 5000,
        refetchInterval: 30000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retryOnMount: false,
        retry: 5,
        enabled: !isAnonymous && enabled,
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        select: data => {
            return {
                ...data,
                isExpired: new Date(data.expire_at) < new Date(),
                remaining: data.remaining,
            };
        },
        onSuccess(data) {
            if (!(data.isExpired || data.remaining == 0)) {
                creditsDone = false;
            } else if (!creditsDone) {
                mixpanel?.track('credits-done', data);
                creditsDone = true;
            }
        },
        onError: err => {
            const axiosError = err as AxiosError;
            const prevData = queryClient.getQueryData<Credit>(queryKey);
            queryClient.setQueryData(queryKey, prevData ?? { remaining: 0, total: 1, expire_at: '' });
            logger.error('Could not fetch credits', { err, status: axiosError.response?.status });
        },
    });
}

export const formatCredits = (credits: number) => Number(credits.toFixed(2));

export const creditsText = (credits: number, creditsToMinutesEnabled = false, unlimitedFirstMonthCreditsEnabled = false) => {
    if (creditsToMinutesEnabled) {
        if (unlimitedFirstMonthCreditsEnabled && credits > 0) {
            return 'Unlimited minutes';
        }
        return credits >= 0 ? `${creditsToVideoMinutes(credits)} minutes left` : `minutes over`;
    }

    if (unlimitedFirstMonthCreditsEnabled && credits > 0) {
        return 'Unlimited credits';
    }

    const text = formatCredits(credits);
    return credits >= 0 ? `${text} credits left` : `${-text} credits over`;
};

export const creditsToVideoMinutes = (credits: number) => {
    const minutes = Math.max(credits, 0) / 4;

    return formatCredits(minutes);
};
